import { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { FeedbackData } from '@/hooks/useFeedbackSurvey';
import {
  SatisfactionStep,
  PurchaseStep,
  FeatureRequestStep,
  OpenFeedbackStep,
} from './FeedbackSteps';

// ─── Types ──────────────────────────────────────────────────────────────────

interface FeedbackSurveyProps {
  open: boolean;
  onSubmit: (data: FeedbackData) => Promise<void>;
  onClose: () => void;
  isLoading: boolean;
}

const TOTAL_STEPS = 4;
const WORD_THRESHOLD = 30;

// ─── Component ──────────────────────────────────────────────────────────────

export function FeedbackSurvey({ open, onSubmit, onClose, isLoading }: FeedbackSurveyProps) {
  // Step state
  const [step, setStep] = useState(1);
  const [satisfaction, setSatisfaction] = useState('');
  const [purchaseExperience, setPurchaseExperience] = useState('');
  const [purchaseDetails, setPurchaseDetails] = useState('');
  const [featureRequests, setFeatureRequests] = useState<string[]>([]);
  const [openFeedback, setOpenFeedback] = useState('');

  // Slide direction for animation
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = backward

  // Computed word count
  const wordCount = useMemo(
    () => openFeedback.trim().split(/\s+/).filter(Boolean).length,
    [openFeedback]
  );

  // Can proceed to next step
  const canGoNext = useMemo(() => {
    switch (step) {
      case 1:
        return satisfaction !== '';
      case 2:
        return purchaseExperience !== '';
      case 3:
      case 4:
        return true;
      default:
        return false;
    }
  }, [step, satisfaction, purchaseExperience]);

  // Navigation handlers
  const goNext = () => {
    if (step < TOTAL_STEPS && canGoNext) {
      setDirection(1);
      setStep((s) => s + 1);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setDirection(-1);
      setStep((s) => s - 1);
    }
  };

  // Submit handler
  const handleSubmit = async () => {
    const data: FeedbackData = {
      satisfaction,
      purchase_experience: purchaseExperience,
      purchase_details: purchaseDetails || undefined,
      feature_requests: featureRequests,
      open_feedback: openFeedback || undefined,
      word_count: wordCount,
    };

    try {
      await onSubmit(data);

      if (wordCount >= WORD_THRESHOLD) {
        toast.success('Thank you! Early access unlocked. Check your profile for the badge.');
      } else {
        toast.success('Thank you for your feedback! You earned the Early Supporter badge.');
      }
    } catch {
      toast.error('Something went wrong. Please try again.');
    }
  };

  // Reset state when dialog closes
  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) return; // Prevent outside click close — only explicit actions close
  };

  // Cancel / close handler
  const handleCancel = () => {
    onClose();
    // Reset state after close animation
    setTimeout(() => {
      setStep(1);
      setDirection(1);
      setSatisfaction('');
      setPurchaseExperience('');
      setPurchaseDetails('');
      setFeatureRequests([]);
      setOpenFeedback('');
    }, 300);
  };

  // Slide animation variants
  const slideVariants = {
    // 25/09/2026: sem opacity 0 na entrada; os botões do passo herdavam invisível.
    enter: (dir: number) => ({
      x: dir > 0 ? 200 : -200,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (dir: number) => ({
      x: dir > 0 ? -200 : 200,
      opacity: 0,
    }),
  };

  // Submit button text
  const submitText =
    wordCount >= WORD_THRESHOLD ? 'Submit & claim early access' : 'Submit';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md mx-auto p-0 gap-0 bg-background border-border/60 rounded-3xl overflow-x-hidden overflow-y-auto"
        hideClose
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        {/* Accessible title (visually hidden) */}
        <DialogTitle className="sr-only">Feedback Survey</DialogTitle>

        {/* ─── Progress Dots ─────────────────────────────────── */}
        <div className="flex justify-center gap-2 pt-5 pb-2">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => {
            const stepNum = i + 1;
            const isActive = stepNum === step;
            const isCompleted = stepNum < step;

            return (
              <div
                key={stepNum}
                className={cn(
                  'h-2 rounded-full transition-all duration-300',
                  isActive
                    ? 'w-8 bg-orange-500'
                    : isCompleted
                      ? 'w-2 bg-orange-500/60'
                      : 'w-2 bg-muted-foreground/30'
                )}
              />
            );
          })}
        </div>

        {/* ─── Step Content ──────────────────────────────────── */}
        <div className="px-5 py-4 min-h-[320px] flex items-center">
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={step}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25, ease: 'easeInOut' }}
              className="w-full"
            >
              {step === 1 && (
                <SatisfactionStep
                  value={satisfaction}
                  onChange={setSatisfaction}
                />
              )}
              {step === 2 && (
                <PurchaseStep
                  value={purchaseExperience}
                  onChange={setPurchaseExperience}
                  details={purchaseDetails}
                  onDetailsChange={setPurchaseDetails}
                />
              )}
              {step === 3 && (
                <FeatureRequestStep
                  value={featureRequests}
                  onChange={setFeatureRequests}
                />
              )}
              {step === 4 && (
                <OpenFeedbackStep
                  value={openFeedback}
                  onChange={setOpenFeedback}
                  wordCount={wordCount}
                />
              )}
            </motion.div>
          </AnimatePresence>
        </div>

        {/* ─── Navigation Bar ────────────────────────────────── */}
        <div className="flex items-center justify-between px-5 pb-5 pt-2">
          {/* Left: Cancel or Back */}
          {step === 1 ? (
            <Button
              variant="ghost"
              className="rounded-xl text-muted-foreground"
              onClick={handleCancel}
              disabled={isLoading}
            >
              Cancel
            </Button>
          ) : (
            <Button
              variant="ghost"
              className="rounded-xl text-muted-foreground"
              onClick={goBack}
              disabled={isLoading}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Back
            </Button>
          )}

          {/* Right: Next or Submit */}
          {step < TOTAL_STEPS ? (
            <Button
              className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white"
              onClick={goNext}
              disabled={!canGoNext}
            >
              Next
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="rounded-xl bg-orange-500 hover:bg-orange-600 text-white"
              onClick={handleSubmit}
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : submitText}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
